/**
 * Target languages that carry the Atlas course track.
 * German and Swiss German stay on their existing sections.
 * English is included here as a course route only.
 */

export const COURSE_TARGET_LANGS = Object.freeze([
  "bg",
  "es",
  "cs",
  "da",
  "de",
  "et",
  "el",
  "en",
  "fr",
  "ga",
  "hr",
  "it",
  "lv",
  "lt",
  "hu",
  "mt",
  "nl",
  "pl",
  "pt",
  "ro",
  "sk",
  "sl",
  "fi",
  "sv",
  "hi",
  "hy",
  "th",
]);

/** English names. Course chrome stays English until the structure settles. */
export const COURSE_LANGUAGE_NAMES = Object.freeze({
  bg: "Bulgarian",
  es: "Spanish",
  cs: "Czech",
  da: "Danish",
  de: "German",
  et: "Estonian",
  el: "Greek",
  en: "English",
  fr: "French",
  ga: "Irish",
  hr: "Croatian",
  it: "Italian",
  lv: "Latvian",
  lt: "Lithuanian",
  hu: "Hungarian",
  mt: "Maltese",
  nl: "Dutch",
  pl: "Polish",
  pt: "Portuguese",
  ro: "Romanian",
  sk: "Slovak",
  sl: "Slovenian",
  fi: "Finnish",
  sv: "Swedish",
  hi: "Hindi",
  hy: "Armenian",
  th: "Thai",
  sq: "Albanian",
  ar: "Arabic",
  zh: "Chinese",
  ka: "Georgian",
  id: "Indonesian",
  ja: "Japanese",
  ko: "Korean",
  ne: "Nepali",
  fa: "Persian",
  ru: "Russian",
  sw: "Swahili",
  tr: "Turkish",
  vi: "Vietnamese",
});

/** Same ASCII slug in every interface language. Existing targets keep their own slugs. */
export const COURSE_TARGET_SLUGS = Object.freeze({
  bg: "bulgarian",
  es: "spanish",
  cs: "czech",
  da: "danish",
  et: "estonian",
  el: "greek",
  fr: "french",
  ga: "irish",
  hr: "croatian",
  it: "italian",
  lv: "latvian",
  lt: "lithuanian",
  hu: "hungarian",
  mt: "maltese",
  nl: "dutch",
  pl: "polish",
  pt: "portuguese",
  ro: "romanian",
  sk: "slovak",
  sl: "slovenian",
  fi: "finnish",
  sv: "swedish",
  hi: "hindi",
  hy: "armenian",
  th: "thai",
  sq: "albanian",
  ar: "arabic",
  zh: "chinese",
  ka: "georgian",
  id: "indonesian",
  ja: "japanese",
  ko: "korean",
  ne: "nepali",
  fa: "persian",
  ru: "russian",
  sw: "swahili",
  tr: "turkish",
  vi: "vietnamese",
});

export const COURSE_TARGET_LANDING = Object.freeze(
  Object.fromEntries(Object.entries(COURSE_TARGET_SLUGS).map(([code, slug]) => [code, `learn-${slug}`])),
);

const COURSE_TARGET_SET = new Set(COURSE_TARGET_LANGS);
const PUBLIC_TARGET_LANGS = Object.freeze(["de", "ch", "es"]);
const PUBLIC_COURSE_TARGETS = new Set();

/**
 * Courses a learner can start, in homepage order, once LL_SHOW_NEW_TARGET_LANGS is on.
 * With the flag off, the homepage and learn menu keep German and Swiss German only.
 */
export const ACTIVE_COURSE_TARGET_LANGS = Object.freeze(["lt", "de", "es", "mt", "hy"]);

export function isCourseTarget(code) {
  return COURSE_TARGET_SET.has(String(code || "").trim().toLowerCase());
}

export function courseLanguageName(code) {
  const c = String(code || "").trim().toLowerCase();
  return COURSE_LANGUAGE_NAMES[c] || "";
}

/**
 * Production default is off. Home-page language buttons, the Course nav item,
 * learn-menu targets, and Course buttons on language pages then stay limited
 * to the existing German and Swiss German site.
 */
export function showNewTargetLangs() {
  const value = String(process.env.LL_SHOW_NEW_TARGET_LANGS || "").trim().toLowerCase();
  return value === "1" || value === "true" || value === "yes";
}

/** Course languages used for the homepage buttons and, with German, the learn menu. */
export function visibleCourseTargetLangs() {
  return ACTIVE_COURSE_TARGET_LANGS;
}

/** Learn menu: German and Swiss German, plus the visible courses when the flag is on. */
export function getNavTargetLangs() {
  const codes = ["de", "ch"];
  if (!showNewTargetLangs()) return Object.freeze(codes);
  for (const code of visibleCourseTargetLangs()) {
    if (!codes.includes(code)) codes.push(code);
  }
  return Object.freeze(codes);
}

export function coursePagesAreHidden(targetCode) {
  const code = String(targetCode || "").trim().toLowerCase();
  if (PUBLIC_TARGET_LANGS.includes(code)) return false;
  return isCourseTarget(code) && !showNewTargetLangs();
}

/** Course sections stay off menus and out of the sitemap while the flag is off. */
export function courseSectionIsUnlisted(targetCode) {
  const code = String(targetCode || "").trim().toLowerCase();
  if (PUBLIC_COURSE_TARGETS.has(code)) return false;
  return isCourseTarget(code) && !showNewTargetLangs();
}
